import { Avatar, Badge, Box, FormControlLabel, Icon, IconButton, Switch } from '@mui/material';
import { Form } from '../../../shared/forms';
import { useCallback, useState } from 'react';
import * as Yup from 'yup';
import { VTextField, VTextFieldCurrency, VTextFieldPercentage } from '../../../shared/forms';
import getValidationErrors from '../../../shared/helpers/getValidationErrors';
import { LayoutRegister } from '../../../shared/layouts';
import { ICreateProduct, IProductView, IUpdateProduct, productService } from '../../../shared/services';
import { useRegisterForm } from '../../../shared/hooks/useRegisterForm';
import { FileUploader } from '../../../shared/components';

const schema = Yup.object().shape({
  description: Yup.string().required(),
  amount: Yup.number().required(),
});

interface IProps {
  businessId: number;
  entityId?: number;
  refetch: () => void;
  onCloseEdit: () => void;
}

export const BusinessProductsRegister: React.FC<IProps> = ({ businessId, entityId: productId, refetch, onCloseEdit }) => {
  const [isActive, setIsActive] = useState(true);
  const [profilePicture, setProfilePicture] = useState<string | undefined>();
  const [openDialogUploadFile, setOpenDialogUploadFile] = useState(false);

  const { formRef, isLoadingData, isSaving, create, update, LoadingSkeleton } = useRegisterForm({
    queryKey: 'product',
    queryFn: (id) => productService.getById(id),
    createFn: (dto: ICreateProduct) => productService.create(dto),
    updateFn: (dto: IUpdateProduct) => productService.update(dto),
    editId: productId,
    onSuccess: () => { refetch(); onCloseEdit(); },
    createMessage: 'Produto criado',
    updateMessage: 'Produto editado',
    onLoadData: (data: IProductView) => {
      setIsActive(data.isActive ?? true);
      setProfilePicture(data.profilePicture);
    },
  });

  const handleSubmit = useCallback(
    async (data: Record<string, unknown>) => {
      try {
        formRef.current?.setErrors({});

        await schema.validate({ description: data.description, amount: data.amount }, { abortEarly: false });

        const base = {
          businessId,
          description: data.description,
          amount: Number(data.amount),
          isActive,
          defaultCommissionPercent: data.defaultCommissionPercent
            ? Number(data.defaultCommissionPercent)
            : undefined,
        };

        if (productId) {
          update({ ...base, id: productId } as IUpdateProduct);
        } else {
          create(base as ICreateProduct);
        }
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          formRef.current?.setErrors(getValidationErrors(err));
        }
      }
    },
    [isActive, businessId, productId, formRef, create, update]
  );

  if (isLoadingData && productId) {
    return <LoadingSkeleton />;
  }

  return (
    <Box>
      <Form ref={formRef} onSubmit={handleSubmit}>
        <LayoutRegister header='Produto' isSubmit isLoading={isSaving}>
          <Box display='flex' flexDirection='column' gap={2}>
            {!!productId && (
              <Box display='flex' justifyContent='center' mb={1}>
                <Badge
                  overlap='circular'
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    <IconButton
                      onClick={() => setOpenDialogUploadFile(true)}
                      sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
                    >
                      <Icon>photo_camera</Icon>
                    </IconButton>
                  }
                >
                  <Avatar src={profilePicture} sx={{ height: 100, width: 100 }} />
                </Badge>
              </Box>
            )}
            <VTextField name='description' label='Descrição' required />
            <VTextFieldCurrency name='amount' label='Valor (R$)' required />
            <VTextFieldPercentage
              name='defaultCommissionPercent'
              label='Comissão padrão (%)'
              helperText='Usado quando não há meta ativa. Deixe em branco para 0%.'
            />
            <FormControlLabel
              control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
              label={isActive ? 'Ativo' : 'Inativo'}
            />
          </Box>
        </LayoutRegister>
      </Form>

      {!!productId && (
        <FileUploader
          open={openDialogUploadFile}
          id={productId}
          refetch={refetch}
          uploadFile={productService.uploadFile}
          deleteFile={productService.deleteFile}
          onDelete={() => setProfilePicture(undefined)}
          onClose={() => setOpenDialogUploadFile(false)}
          onUploadSuccess={(url) => setProfilePicture(url)}
        />
      )}
    </Box>
  );
};
